import { lightenColor } from "../../../../utils/colors.ts"

// The backend computes background_color/border_color/text_color as
// SerializerMethodFields — 100% server-derived from category, with no
// writable field on the Event model. A per-event custom color therefore
// cannot round-trip through the API today; this persists it client-side,
// keyed by event id, so it survives a refresh on this browser at least.
// Every existing semantic color in this app pairs a light background with a
// black text color (see ezbuild_back's calendars/utils.py) — custom colors
// follow the same rule so contrast never depends on which hue was picked.
const STORAGE_KEY = "calendar:event-colors"
const TEXT_COLOR = "#000000"
const BACKGROUND_TINT_PERCENT = 88

interface EventColorTriple {
  background_color: string
  border_color: string
  text_color: string
}

interface EventColorPaletteEntry {
  key: string
  label: string
  hex: string
}

export const EVENT_COLOR_PALETTE: EventColorPaletteEntry[] = [
  { key: "teal", label: "Teal", hex: "#14B8A6" },
  { key: "blue", label: "Blue", hex: "#3B82F6" },
  { key: "purple", label: "Purple", hex: "#A855F7" },
  { key: "green", label: "Green", hex: "#22C55E" },
  { key: "amber", label: "Amber", hex: "#F59E0B" },
  { key: "orange", label: "Orange", hex: "#FB923C" },
  { key: "red", label: "Red", hex: "#EF4444" },
  { key: "pink", label: "Pink", hex: "#EC4899" },
  { key: "indigo", label: "Indigo", hex: "#6366F1" },
  { key: "slate", label: "Slate", hex: "#64748B" }
]

// The one place a hex accent becomes a full renderable triple, whether it
// came from the predefined palette or a hand-picked custom color — so a
// palette pick and a custom pick that land on the same hex look identical.
export const colorTripleFor = (hex: string): EventColorTriple => ({
  background_color: lightenColor(hex, BACKGROUND_TINT_PERCENT),
  border_color: hex,
  text_color: TEXT_COLOR
})

const readStore = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const writeStore = (store: Record<string, string>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Private browsing / storage disabled / quota exceeded: the color just
    // won't persist. Not worth surfacing an error for a cosmetic preference.
  }
}

// Returns the effective color for an event: its custom pick if one was ever
// saved for this id, on this browser, otherwise null so the caller falls
// back to the event's own server-computed type color.
export const getStoredEventColor = (
  eventId: string | undefined | null
): EventColorTriple | null => {
  if (!eventId) return null
  const hex = readStore()[eventId]
  return hex ? colorTripleFor(hex) : null
}

export const getStoredEventColorHex = (
  eventId: string | undefined | null
): string | null => {
  if (!eventId) return null
  return readStore()[eventId] || null
}

export const setStoredEventColor = (
  eventId: string | undefined | null,
  hex: string | null | undefined
): void => {
  if (!eventId) return
  const store = readStore()

  if (!hex) {
    delete store[eventId]
  } else {
    store[eventId] = hex
  }

  writeStore(store)
}
